import type { CommunitySummary } from '@/features/communities/api/communities.api'
import type { FeedPost } from '@/features/feed/api/types'
import type { ThoughtContent } from '@/features/thought/lib/thought'
import { request } from '@/shared/api/request'
import { camelize } from '@/shared/lib/camelize'
import { readHours, type PageHours } from '../lib/page-hours'
import { PAGE_KIND } from '@/shared/lib/pageKinds'

export interface UserWallet {
  balance?: number | null
  incoming?: number | null
  outgoing?: number | null
  /** Nightly earnings awaiting the monthly settlement - visible, not spendable. */
  pending?: number | null
}

export interface ProfilePostSummary {
  id: string
  mediaType?: number | null
  mediaUrls?: string | null
  thumbnailUrl?: string | null
  likeCount?: number | null
  viewCount?: number | null
  /** Backend PostStatus: Created = 10, InProgress = 20, Published = 30. */
  status?: number | null
  /** Admin blocked the post - only the owner receives it (BLOCKED stamp). */
  isBlocked?: boolean | null
  /** Admin marked the post as fake news (FAKE stamp; creator can't delete). */
  isFakeNews?: boolean | null
  /** The author switched downloads off. */
  downloadsDisabled?: boolean | null
  /** Set when the post was published in a community: the grid shows a "#slug" tag. */
  communityId?: string | null
  communitySlug?: string | null
  communityName?: string | null
  /** The card of a Thought post: the grid draws it instead of a thumbnail. */
  thought?: ThoughtContent | null
  /** A Snap (live or expired): listed by the Snaps tab, never in the posts grid. */
  isSnap?: boolean | null
}

/** GET profile/deleted-posts: one page of the Deleted posts list. */
export interface DeletedPostsResponse {
  data: ProfilePostSummary[]
  total: number
  /** Days a deleted post stays restorable (PostDeletionGraceDays). */
  graceDays?: number | null
}

export function isVideoSummary(post: ProfilePostSummary): boolean {
  return post.mediaType === 20
}

/** Media is still being processed - no playable content yet. */
export function isProcessingSummary(post: ProfilePostSummary): boolean {
  return post.status != null && post.status < 30
}

export function summaryThumbnail(post: ProfilePostSummary): string | null {
  const first = firstMediaUrl(post)
  return isVideoSummary(post) ? (post.thumbnailUrl ?? first) : first
}

export function firstMediaUrl(post: ProfilePostSummary): string | null {
  const raw = post.mediaUrls
  if (!raw) return null
  try {
    const decoded: unknown = JSON.parse(raw)
    if (Array.isArray(decoded)) return (decoded[0] as string) ?? null
  } catch {
    /* bare URL */
  }
  return raw
}

/** Backend PageRoles enum: the viewer's (or a staff row's) role on a page. */
export const PAGE_ROLE = { owner: 10, editor: 20, moderator: 30 } as const

export interface ProfilePageSummary {
  id: string
  username?: string | null
  name?: string | null
  avatarUrl?: string | null
  coverUrl?: string | null
  /** The cover set (carousel); coverUrl is always its first entry. */
  coverUrls?: string[] | null
  followersCount?: number | null
  postsCount?: number | null
  /** Admin blocked the page - only the owner's lists carry it (BLOCKED stamp). */
  isBlocked?: boolean | null
  /**
   * The page's audience (a country XOR a region flag): posts published on
   * the page inherit it, so the post editor shows it instead of a picker.
   */
  countryCode?: string | null
  region?: number | null
  /** PageKind raw value (0 topic, 20 business). */
  kind?: number | null
  /** Business pages: the library track looping under the page (pages/my rows). */
  soundId?: string | null
  soundName?: string | null
  /** Business pages: how many locations the page has (pages/my rows). */
  locationsCount?: number | null
  /** The page owner; on the own list an administered page carries someone else here. */
  ownerId?: string | null
  ownerUsername?: string | null
  ownerDisplayName?: string | null
  isOwner?: boolean | null
  /** PAGE_ROLE of the viewer on this page (own list only). */
  role?: number | null
  /** Search-only words (pages/my and my-followed rows). */
  keywords?: string | null
}

/** A page the user administers for someone else (the own list carries those too). */
export function isAdministeredPage(page: ProfilePageSummary): boolean {
  return page.isOwner === false && page.role != null
}

/** Row of GET /pages/{id}/admins - the owner (role 10) plus the editors. */
export interface PageAdmin {
  userId: string
  username?: string | null
  displayName?: string | null
  avatarUrl?: string | null
  /** PAGE_ROLE raw value. */
  role?: number | null
}

export function isPageOwnerRow(admin: PageAdmin): boolean {
  return admin.role === PAGE_ROLE.owner
}

export function pageAdminLabel(admin: PageAdmin): string {
  return admin.displayName || admin.username || 'User'
}

export interface PageDetails {
  id: string
  name?: string | null
  username?: string | null
  avatarUrl?: string | null
  coverUrl?: string | null
  /** The cover set shown as a carousel; coverUrl is always its first entry. */
  coverUrls?: string[] | null
  bio?: string | null
  category?: string | null
  website?: string | null
  /** The page's country (ISO 3166-1 alpha-2), picked at creation. */
  countryCode?: string | null
  /** Region audience instead of a country (one CountryRegions flag). */
  region?: number | null
  /** PageKind raw value (0 topic, 20 business, 30 business chain). */
  kind?: number | null
  /** Business kinds: the page's kind of place (LocationType), shared by every location. */
  locationType?: number | null
  /** The library track looping under the page while it is open (every kind). */
  soundId?: string | null
  soundUrl?: string | null
  soundName?: string | null
  /** Business pages: the locations in creation order ([] for a Topic page). */
  locations?: PageLocation[] | null
  locationsCount?: number | null
  /**
   * A Business (20) page: always its single location. A chain: set only when
   * the page was fetched through pages/by-location/{id} (the location the
   * visitor opened from a Map pin, a travel place, a search hit).
   */
  location?: PageLocation | null
  followersCount?: number | null
  postsCount?: number | null
  isVerified?: boolean | null
  isFollowing?: boolean | null
  /** Owner or admin: may edit the page and publish on it. */
  isAdmin?: boolean | null
  /** The owner alone deletes the page or its posts and manages the admins. */
  isOwner?: boolean | null
  /** Owning user - drives the "Managed by" card and the Message button. */
  ownerId?: string | null
  ownerUsername?: string | null
  ownerDisplayName?: string | null
  ownerAvatarUrl?: string | null
  ownerIsVerified?: boolean | null
  /** Admin blocked the page - the server only returns it to the owner (support banner). */
  isBlocked?: boolean | null
  /** Search-only words: sent to the page's admins only (null for everyone else). */
  keywords?: string | null
}

/** One row of GET pages/by-location/{id}/ratings. */
export interface LocationRating {
  id: string
  userId: string
  username?: string | null
  displayName?: string | null
  avatarUrl?: string | null
  isVerified?: boolean | null
  stars: number
  comment?: string | null
  /** ISO. */
  ratedAt: string
  /** The caller's own rating. */
  isOwn?: boolean | null
}

/**
 * One location of a Business page: where it is, its own pictures, the
 * weekday discounts and the viewer's "I've been here". The page fields are
 * denormalized copies so a location can be shown without its page.
 */
export interface PageLocation {
  id: string
  pageId: string
  name: string
  /** LocationType raw value (group x 1000 + n). */
  locationType: number
  address?: string | null
  city?: string | null
  /** The place's phone number as the admin typed it (digits, +, spaces, . - ( )); null when none. */
  phone?: string | null
  latitude: number
  longitude: number
  /** Percent off per weekday ("mon".."sun", 1-90); always an object, maybe empty. */
  discounts: Record<string, number>
  /** Today's discount by the server's UTC day (clients prefer the device's day). */
  discountToday?: number | null
  /** The location's own pictures, in order. */
  pictureUrls: string[]
  /** Page admins only (0 otherwise): pictures still in the upload pipeline. */
  picturesPendingCount?: number | null
  /** The caller marked "I've been here". */
  isVisited?: boolean | null
  /** Average stars (0 without ratings); a like IS the star rating. */
  ratingAvg?: number | null
  ratingsCount?: number | null
  /** The caller's own 1-5, null when never rated. */
  myStars?: number | null
  /** The caller's rating cannot change before this (ISO). */
  myRatingLockedUntil?: string | null
  /** The caller's own comment on their rating, null without. */
  myComment?: string | null
  /** Opening hours; null = no timetable. */
  hours?: PageHours | null
  pageUsername: string
  pageName: string
  pageAvatarUrl?: string | null
  pageIsVerified?: boolean | null
  createdAt?: string | null
}

/** Body of POST/PUT pages/{pageId}/locations: the pin is required (0,0 is refused). */
export interface SavePageLocationInput {
  /** Ignored on a Business (20) page: the location is named after the page. */
  name?: string | null
  /** Ignored when the page has a kind of place (always, after the wizard). */
  locationType?: number | null
  address?: string | null
  city?: string | null
  /** PUT: omitted or null = unchanged, "" = removed. The server keeps it only with 3+ digits. */
  phone?: string | null
  latitude: number
  longitude: number
  /** Omitted or null = unchanged (PUT), {} = none. */
  discounts?: Record<string, number> | null
  /** PUT: the ordered keep-list of EXISTING urls (omitted = unchanged). */
  pictureUrls?: string[] | null
  /** Omitted or null = unchanged (PUT), an empty timetable = none. */
  hours?: PageHours | null
}

export interface UserProfile {
  id: string
  username: string
  displayName?: string | null
  avatarUrl?: string | null
  bio?: string | null
  externalLink?: string | null
  country?: string | null
  countryCode?: string | null
  followersCount?: number | null
  followingCount?: number | null
  postsCount?: number | null
  /** The user's Snaps (live or expired), kept out of postsCount and posts. */
  snapsCount?: number | null
  totalLikes?: number | null
  totalViews?: number | null
  isOwnProfile?: boolean | null
  isFollowing?: boolean | null
  wallet?: UserWallet | null
  posts?: ProfilePostSummary[] | null
  savedPosts?: ProfilePostSummary[] | null
  pages?: ProfilePageSummary[] | null
  /** Communities the user owns, biggest first: the header previews the first two. */
  communities?: CommunitySummary[] | null
  /** How many communities the user owns in total (the header shows "More" past two). */
  communitiesCount?: number | null
  /** Last request the user made, stamped every few minutes: the header's "last seen" line. */
  lastSeenAt?: string | null
  /** UserNature bit flags: 1 = Pioneer, 2 = Premium, 4 = Red, 8 = Blue - drives the avatar badge. */
  nature?: number | null
  /** Achievement badges earned (every profile): the gold count on the avatar, hidden at 0. */
  badgesCount?: number | null
  /** Admin-granted platform badge (users.is_eleva_verified on the backend). */
  isVerified?: boolean | null
  /** KYC approved AND published by the user - shows the green badge. */
  kycVerified?: boolean | null
  /** Own profile only - the settings "Public KYC badge" toggle. */
  isKycPublic?: boolean | null
  /** Own profile only - the avatar upload pipeline is still processing. */
  avatarProcessing?: boolean | null
  /** Own profile only - ISO date when the next avatar change unlocks; null = first change still free. */
  avatarChangeableAt?: string | null
  /** Own profile only - ISO date when the next username change unlocks; null = first change still free. */
  usernameChangeableAt?: string | null
  /** Own profile only - false when an admin switched off token earnings (profile shows a notice). */
  canEarnTokens?: boolean | null
  /** Most days in a row this account opened Eleva (every profile). */
  longestStreak?: number | null
  /** Own profile only - days in a row up to and including today. */
  currentStreak?: number | null
  /** Own profile only - accepted-friend count (others never expose it). */
  friendsCount?: number | null
  /** Own profile only: how many pages the user follows (social section row). */
  followedPagesCount?: number | null
  /** Other profiles only - my relationship: none | pending_sent | pending_received | friends. */
  friendStatus?: string | null
  /**
   * I am the one holding the block, so the profile offers an unblock instead of
   * the follow/message/friend actions. A block the other side holds is not mine
   * to lift, so it leaves the actions in place.
   */
  blockedByMe?: boolean | null
  /** Own profile only - backend-synced settings switches. */
  settings?: ProfilePrivacySettings | null
}

/** Profile "about me" entry (kind: 0 education, 1 work, 2 vacation, 3 other). */
export interface AboutItem {
  id: string
  kind: number
  name: string
  startYear?: number | null
  endYear?: number | null
}

/** Own-profile settings summary - only the fields the web app reads. */
export interface ProfilePrivacySettings {
  /** False = the "do not accept gifts" switch is on. */
  acceptsGifts?: boolean | null
  /** ProfileConnections: 1 everyone, 2 friends - 2 = "do not accept messages" on. */
  whoCanMessage?: number | null
  /**
   * FriendRequestPrivacy flags: 0 custom-none, 1 everyone, 2 friends of
   * friends, 4 people I follow, 8 the explicit "no one" switch.
   */
  friendRequestPrivacy?: number | null
  /** Social sanity: seconds the feed holds on an image / video post (0 = off). */
  imageWatchSeconds?: number | null
  videoWatchSeconds?: number | null
  /** The "show last seen" switch (two-way: off also hides everyone's from me). */
  showLastSeen?: boolean | null
  /** When that switch may be flipped again; null = free to flip now. */
  lastSeenChangeableAt?: string | null
}

/** GET /premium/status and the response of subscribe/cancel. */
export interface PremiumStatus {
  isActive: boolean
  /** PremiumMethod enum: 1 = App Store, 2 = tokens, 3 = card via Stripe (web), 4 = Google Play. */
  method?: number | null
  nextBillingAt?: string | null
  monthlyTokens?: number | null
  cancelAtPeriodEnd?: boolean | null
}

/** complimentary = granted by an Eleva admin: nothing renews, nothing is billed, nothing to cancel. */
export const PREMIUM_METHOD = { store: 1, tokens: 2, stripe: 3, googlePlay: 4, complimentary: 5 } as const

/** Store-billed subscriptions can only be cancelled inside the mobile app. */
export function isStoreManagedPremium(status: PremiumStatus): boolean {
  return status.method === PREMIUM_METHOD.store || status.method === PREMIUM_METHOD.googlePlay
}

/**
 * Row from GET /wallet/transactions. `tokens` is signed - negative means the
 * tokens left this wallet. The counterparty fields are null for system
 * wallets (store top-ups etc.); `otherWalletType` labels those.
 */
export interface WalletTransactionEntry {
  id: string
  /** TransactionType enum: 10 gift, 20 settlement, 30 purchase, 100 other. */
  type?: number | null
  tokens?: number | null
  /** Earnings not yet settled - rows render a PENDING chip. */
  isPending?: boolean | null
  createdAt?: string | null
  otherUsername?: string | null
  otherAvatarUrl?: string | null
  otherWalletType?: number | null
}

export function isIncomingTransaction(tx: WalletTransactionEntry): boolean {
  return (tx.tokens ?? 0) >= 0
}

/** Entry in followers / following / blocked lists (loose field names). */
export interface FollowUser {
  id?: string | null
  userId?: string | null
  username?: string | null
  displayName?: string | null
  avatarUrl?: string | null
}

export function followUserId(user: FollowUser): string {
  return user.id ?? user.userId ?? user.username ?? ''
}

export function followUserLabel(user: FollowUser): string {
  return user.displayName || user.username || 'User'
}

export interface RankingEntry {
  rank: number
  userId?: string | null
  username?: string | null
  avatarUrl?: string | null
  score?: number | null
  isCurrentUser?: boolean | null
}

/**
 * One chart bar. `date` is the bucket: a UTC day ("yyyy-MM-dd") for 7D, the
 * week's Monday otherwise. `value` is a count, or a decimal token amount on
 * the tokens chart.
 */
export interface ChartPoint {
  date?: string | null
  value?: number | null
}

export function chartAmount(point: ChartPoint): number {
  return point.value ?? 0
}

/**
 * The period codes GET analytics/creator accepts, one per chip:
 * 7D 7d, 4W 4w, 3M 12w, 6M 26w, 1Y 52w.
 */
export type AnalyticsPeriod = '7d' | '4w' | '12w' | '26w' | '52w'

/** The chart bucket size: 7D by the day, the week periods by the week. */
export type AnalyticsGranularity = 'day' | 'week'

/**
 * GET analytics/creator (own account only). Engagement is what the user's
 * posts received during the period; every tokens_* and wallet_* figure is a
 * decimal (fractions of a token), the rest are integer counts. *Previous =
 * the period right before, for the deltas.
 */
export interface AnalyticsData {
  period?: string | null
  granularity?: string | null
  /** Inclusive UTC dates ("yyyy-MM-dd") of the period on screen. */
  rangeStart?: string | null
  rangeEnd?: string | null
  viewsCurrent?: number | null
  viewsPrevious?: number | null
  likesCurrent?: number | null
  likesPrevious?: number | null
  commentsCurrent?: number | null
  commentsPrevious?: number | null
  sharesCurrent?: number | null
  sharesPrevious?: number | null
  giftsCurrent?: number | null
  giftsPrevious?: number | null
  /** The user's own non-repost posts published in the period (video excludes Artwork). */
  postsTotal?: number | null
  postsPhoto?: number | null
  postsVideo?: number | null
  postsArtwork?: number | null
  postsThought?: number | null
  viewsChart?: ChartPoint[] | null
  likesChart?: ChartPoint[] | null
  sharesChart?: ChartPoint[] | null
  commentsChart?: ChartPoint[] | null
  /** Income = gifts + creator ads. */
  tokensIncomeCurrent?: number | null
  tokensIncomePrevious?: number | null
  /** Gift tokens received, net of the referral commission the author passes on. */
  tokensGifts?: number | null
  /** Ad revenue earned by the user's posts. */
  tokensCreatorAds?: number | null
  /** Tokens spent on gifts. */
  tokensSpent?: number | null
  /** Income per bucket. */
  tokensChart?: ChartPoint[] | null
  walletAvailable?: number | null
  walletPending?: number | null
  followersCount?: number | null
}

const PROFILE = '/api/eleva/profile'
const PAGES = '/api/eleva/pages'
const WALLET = '/api/eleva/wallet'

/**
 * GET /wallet/kyc/status - in_progress means a Didit verification session is
 * open (resumable); pending means the decision is with Didit or the staff;
 * expired flows start over without paying the fee again.
 */
export interface KycStatus {
  status: 'none' | 'pending' | 'in_progress' | 'approved' | 'rejected' | 'expired'
  /** False while the KycEnabled app setting is off. */
  available?: boolean | null
  balance?: number | null
  minTokens?: number | null
  feeTokens?: number | null
  eligible?: boolean | null
}

/**
 * Row from GET /wallet/withdrawals. `amountCents` is the fiat value finance
 * pays out manually; status walks pending -> approved -> paid (rejected
 * refunds the tokens).
 */
export interface WithdrawalEntry {
  id: string
  tokens?: number | null
  amountCents?: number | null
  status?: 'pending' | 'approved' | 'paid' | 'rejected' | string | null
  createdAt?: string | null
}

/**
 * Not seeded in app_settings, so the value never reaches clients - the same
 * 100-token floor the backend falls back to (MinWithdrawTokens).
 */
export const MIN_WITHDRAW_TOKENS = 100

export const walletApi = {
  /** Requests a manual payout; the server enforces KYC + minimum + balance. */
  async withdraw(input: { tokens?: number; all: boolean }): Promise<void> {
    await request({
      method: 'POST',
      url: `${WALLET}/withdraw`,
      data: { all: input.all, ...(input.all ? {} : { tokens: input.tokens }) },
    })
  },

  async getWithdrawals(): Promise<WithdrawalEntry[]> {
    const data = await request<unknown>({ url: `${WALLET}/withdrawals` })
    return camelize<WithdrawalEntry[]>(data)
  },
}

/** One Stripe price as the web shows it: amount in hundredths of the major unit. */
export interface StripePriceInfo {
  priceId: string
  amountCents: number
  currency: string
}

export interface StripeTokenPack extends StripePriceInfo {
  tokens: number
}

/** GET /payments/stripe/catalog - `available` is false while Stripe is off. */
export interface StripeCatalog {
  available: boolean
  black?: StripePriceInfo | null
  packages: StripeTokenPack[]
}

/** GET /payments/stripe/session/{id} - what became of a Checkout Session. */
export interface PaymentConfirmation {
  status: 'completed' | 'pending' | 'failed'
  kind: 'tokens' | 'premium'
  tokensAdded?: number | null
  balance?: number | null
  alreadyProcessed?: boolean | null
}

const PAYMENTS = '/api/eleva/payments'

/**
 * Card payments through Stripe Checkout (hosted page): the backend opens a
 * session and the browser is sent to its URL; Stripe brings the user back to
 * /payments/return, which confirms the session (idempotent with the webhook).
 */
export const paymentsApi = {
  async getCatalog(): Promise<StripeCatalog> {
    const data = await request<unknown>({ url: `${PAYMENTS}/stripe/catalog` })
    return camelize<StripeCatalog>(data)
  },

  /** Stripe Checkout for a token pack; returns the hosted payment URL. */
  async checkoutTokens(tokens: number): Promise<{ url: string; sessionId: string }> {
    const data = await request<unknown>({
      method: 'POST',
      url: `${PAYMENTS}/stripe/checkout`,
      data: { kind: 'tokens', tokens },
    })
    return camelize<{ url: string; sessionId: string }>(data)
  },

  /** Stripe Checkout for the monthly Eleva Black subscription. */
  async checkoutPremium(): Promise<{ url: string; sessionId: string }> {
    const data = await request<unknown>({
      method: 'POST',
      url: `${PAYMENTS}/stripe/checkout`,
      data: { kind: 'premium' },
    })
    return camelize<{ url: string; sessionId: string }>(data)
  },

  async confirmSession(sessionId: string): Promise<PaymentConfirmation> {
    const data = await request<unknown>({
      url: `${PAYMENTS}/stripe/session/${encodeURIComponent(sessionId)}`,
    })
    return camelize<PaymentConfirmation>(data)
  },

  /** Stripe Billing Portal (invoices, card on file) for card subscribers. */
  async openPortal(): Promise<{ url: string }> {
    const data = await request<unknown>({ method: 'POST', url: `${PAYMENTS}/stripe/portal` })
    return camelize<{ url: string }>(data)
  },
}

/** "$14.99" / "14,99 €" from a catalog amount, in the viewer's locale. */
export function formatMoney(amountCents: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amountCents / 100)
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency}`
  }
}

export const kycApi = {
  async getStatus(): Promise<KycStatus> {
    const data = await request<unknown>({ url: `${WALLET}/kyc/status` })
    return camelize<KycStatus>(data)
  },

  /**
   * Charges the one-time fee (first start only) and opens a Didit
   * verification session, returning its hosted-flow url. Resuming an
   * in_progress flow mints a fresh session. `url` is absent when Didit
   * isn't configured - the request then queues for manual staff review.
   */
  async start(): Promise<{ status: string; url?: string | null }> {
    const data = await request<unknown>({ method: 'POST', url: `${WALLET}/kyc/start` })
    return camelize<{ status: string; url?: string | null }>(data)
  },
}

/** Snake-cased "about me" payload; optional years only ride when set. */
function aboutBody(input: {
  kind: number
  name: string
  startYear?: number | null
  endYear?: number | null
}): Record<string, unknown> {
  const body: Record<string, unknown> = { kind: input.kind, name: input.name }
  if (input.startYear != null) body.start_year = input.startYear
  if (input.endYear != null) body.end_year = input.endYear
  return body
}

/** A location as the app keeps it: the array and object fields never missing. */
function normalizeLocation(location: PageLocation): PageLocation {
  return {
    ...location,
    discounts: location.discounts ?? {},
    pictureUrls: location.pictureUrls ?? [],
    hours: readHours(location.hours),
  }
}

/** A page's locations normalized ([] for a Topic page), the opened one included. */
function normalizePage(page: PageDetails): PageDetails {
  return {
    ...page,
    locations: (page.locations ?? []).map(normalizeLocation),
    location: page.location ? normalizeLocation(page.location) : null,
  }
}

/** SavePageLocation on the wire (snake_case). */
function locationBody(input: SavePageLocationInput): Record<string, unknown> {
  return {
    ...(input.name != null ? { name: input.name } : {}),
    ...(input.locationType != null ? { location_type: input.locationType } : {}),
    address: input.address ?? null,
    city: input.city ?? null,
    ...(input.phone !== undefined ? { phone: input.phone } : {}),
    latitude: input.latitude,
    longitude: input.longitude,
    ...(input.discounts !== undefined ? { discounts: input.discounts } : {}),
    ...(input.pictureUrls !== undefined ? { picture_urls: input.pictureUrls } : {}),
    ...(input.hours != null ? { hours: input.hours } : {}),
  }
}

export const profileApi = {
  async getUserProfile(userId: string): Promise<UserProfile> {
    const data = await request<unknown>({ url: `${PROFILE}/id/${userId.toLowerCase()}` })
    return camelize<UserProfile>(data)
  },

  /**
   * One page of a user's grid, newest first. The posts list leaves Snaps
   * out; `snaps` lists only the user's Snaps (live or expired) instead.
   */
  async getUserPosts(
    userId: string,
    skip = 0,
    take = 30,
    snaps = false,
  ): Promise<{ data: ProfilePostSummary[]; total: number }> {
    const data = await request<unknown>({
      url: `${PROFILE}/${userId.toLowerCase()}/posts`,
      params: snaps ? { skip, take, snaps: true } : { skip, take },
    })
    const response = camelize<{ data?: ProfilePostSummary[] | null; total?: number | null }>(data)
    return { data: response.data ?? [], total: response.total ?? 0 }
  },

  async updateProfile(input: {
    displayName?: string
    bio?: string
    externalLink?: string
  }): Promise<void> {
    const body: Record<string, string> = {}
    if (input.displayName !== undefined) body.display_name = input.displayName
    if (input.bio !== undefined) body.bio = input.bio
    if (input.externalLink !== undefined) body.external_link = input.externalLink
    if (Object.keys(body).length === 0) return
    await request({ method: 'PUT', url: `${PROFILE}/update`, data: body })
  },

  /** Sets the profile country (ISO 3166-1 alpha-2, picked in settings). */
  async updateCountry(code: string): Promise<void> {
    await request({ method: 'PUT', url: `${PROFILE}/country`, data: { country_code: code } })
  },

  /** Publishes/hides the "KYC verified" profile badge. */
  async setKycPublic(enabled: boolean): Promise<void> {
    await request({ method: 'POST', url: `${PROFILE}/kyc-public`, data: { enabled } })
  },

  /**
   * The "show last seen" privacy switch (two-way). The server locks it for
   * LastSeenToggleCooldownHours after every flip and answers
   * last_seen_change_cooldown (with the unlock time) while locked.
   */
  async setLastSeenVisibility(enabled: boolean): Promise<void> {
    await request({ method: 'POST', url: `${PROFILE}/last-seen-visibility`, data: { enabled } })
  },

  /** enabled=false is the "do not accept gifts" settings switch. */
  async setGiftsAcceptance(enabled: boolean): Promise<void> {
    await request({ method: 'POST', url: `${PROFILE}/gifts-acceptance`, data: { enabled } })
  },

  /** friendsOnly=true is the "do not accept messages" settings switch. */
  async setMessagePrivacy(friendsOnly: boolean): Promise<void> {
    await request({
      method: 'POST',
      url: `${PROFILE}/message-privacy`,
      data: { friends_only: friendsOnly },
    })
  },

  /**
   * Sets the "friend requests can be from" mask (FriendRequestPrivacy
   * flags). Returns the value the server stored - Everyone and the explicit
   * NoOne switch collapse to their pure form there.
   */
  /**
   * Social sanity dwell times (images 0-10 s, videos 0-30 s, 0 = off).
   * Returns the clamped values the server stored.
   */
  async setSocialSanity(
    imageSeconds: number,
    videoSeconds: number,
  ): Promise<{ image: number; video: number }> {
    const response = await request<{ image_watch_seconds?: number; video_watch_seconds?: number }>({
      method: 'POST',
      url: `${PROFILE}/social-sanity`,
      data: { image_seconds: imageSeconds, video_seconds: videoSeconds },
    })
    return {
      image: response.image_watch_seconds ?? imageSeconds,
      video: response.video_watch_seconds ?? videoSeconds,
    }
  },

  async setFriendRequestPrivacy(privacy: number): Promise<number> {
    const response = await request<{ friend_request_privacy?: number }>({
      method: 'POST',
      url: `${PROFILE}/friend-request-privacy`,
      data: { privacy },
    })
    return response.friend_request_privacy ?? privacy
  },

  // "About me" entries

  /** Public "about me" entries of any profile - the "More info" panel and
   *  the owner's editor fetch them on demand (they no longer ride in the
   *  profile response). */
  async getAboutItems(userId: string): Promise<AboutItem[]> {
    const response = await request<{ data?: unknown[] }>({
      url: `${PROFILE}/${userId.toLowerCase()}/about`,
    })
    return (response.data ?? []).map((row) => camelize<AboutItem>(row))
  },

  async addAboutItem(input: {
    kind: number
    name: string
    startYear?: number | null
    endYear?: number | null
  }): Promise<AboutItem> {
    const data = await request<unknown>({
      method: 'POST',
      url: `${PROFILE}/about`,
      data: aboutBody(input),
    })
    return camelize<AboutItem>(data)
  },

  async updateAboutItem(
    id: string,
    input: { kind: number; name: string; startYear?: number | null; endYear?: number | null },
  ): Promise<AboutItem> {
    const data = await request<unknown>({
      method: 'PUT',
      url: `${PROFILE}/about/${id.toLowerCase()}`,
      data: aboutBody(input),
    })
    return camelize<AboutItem>(data)
  },

  async deleteAboutItem(id: string): Promise<void> {
    await request({ method: 'DELETE', url: `${PROFILE}/about/${id.toLowerCase()}` })
  },

  /** Interests picked in settings, as PostMood ids. */
  async getInterests(): Promise<number[]> {
    const data = await request<Record<string, unknown>>({ url: `${PROFILE}/interests` })
    return Array.isArray(data?.preferred_moods) ? (data.preferred_moods as number[]) : []
  },

  async saveInterests(moods: number[]): Promise<void> {
    await request({
      method: 'POST',
      url: `${PROFILE}/interests`,
      data: { preferred_moods: moods },
    })
  },

  async getFollowers(userId: string): Promise<FollowUser[]> {
    const data = await request<unknown>({ url: `${PROFILE}/${userId}/followers` })
    return camelize<FollowUser[]>(data)
  },

  async getFollowing(userId: string): Promise<FollowUser[]> {
    const data = await request<unknown>({ url: `${PROFILE}/${userId}/following` })
    return camelize<FollowUser[]>(data)
  },

  async removeFollower(userId: string): Promise<void> {
    await request({ method: 'DELETE', url: `${PROFILE}/follower/${userId}` })
  },

  /** Unfollow = the same follow toggle endpoint. Returns the resulting state,
   *  so a caller can render from the server's answer instead of guessing. */
  async unfollow(userId: string): Promise<boolean> {
    const data = await request<Record<string, unknown>>({
      method: 'POST',
      url: `${PROFILE}/follow/${userId}`,
    })
    return Boolean(data?.is_following ?? data?.isFollowing)
  },

  /**
   * Posts the caller deleted themselves and may bring back (Settings >
   * Deleted posts). `graceDays`: how long a deleted post stays listed and
   * restorable before it is erased for good.
   */
  async getDeletedPosts(skip = 0, take = 30): Promise<DeletedPostsResponse> {
    const data = await request<unknown>({
      url: `${PROFILE}/deleted-posts`,
      params: { skip, take },
    })
    return camelize<DeletedPostsResponse>(data)
  },

  /** Undeletes the selection; resolves to the ids that actually came back. */
  async restorePosts(postIds: string[]): Promise<string[]> {
    const data = await request<Record<string, unknown>>({
      method: 'POST',
      url: `${PROFILE}/deleted-posts/restore`,
      data: { post_ids: postIds },
    })
    const restored = data?.restored
    return Array.isArray(restored) ? restored.map(String) : []
  },

  async getBlockedUsers(): Promise<FollowUser[]> {
    const data = await request<unknown>({ url: `${PROFILE}/blocked` })
    return camelize<FollowUser[]>(data)
  },

  async unblockUser(userId: string): Promise<void> {
    await request({ method: 'DELETE', url: `${PROFILE}/block/${userId}` })
  },

  // ---- Premium ----

  async getPremiumStatus(): Promise<PremiumStatus> {
    const data = await request<unknown>({ url: '/api/eleva/premium/status' })
    return camelize<PremiumStatus>(data)
  },

  /** `method`: 1 = store, 2 = tokens. Throws when the wallet can't cover the first month. */
  async subscribePremium(method: number): Promise<PremiumStatus> {
    const data = await request<unknown>({
      method: 'POST',
      url: '/api/eleva/premium/subscribe',
      data: { method },
    })
    return camelize<PremiumStatus>(data)
  },

  async cancelPremium(): Promise<PremiumStatus> {
    const data = await request<unknown>({ method: 'POST', url: '/api/eleva/premium/cancel' })
    return camelize<PremiumStatus>(data)
  },

  // ---- Wallet ----

  async getWalletTransactions(limit = 50, offset = 0): Promise<WalletTransactionEntry[]> {
    const data = await request<unknown>({
      url: '/api/eleva/wallet/transactions',
      params: { limit, offset },
    })
    return camelize<WalletTransactionEntry[]>(data)
  },

  // ---- Pages ----

  async getUserPages(userId?: string): Promise<ProfilePageSummary[]> {
    const data = await request<unknown>({
      url: `${PAGES}/my`,
      params: userId ? { user_id: userId } : undefined,
    })
    return camelize<ProfilePageSummary[]>(data)
  },

  async getPage(username: string): Promise<PageDetails> {
    const data = await request<unknown>({ url: `${PAGES}/${username}` })
    return normalizePage(camelize<PageDetails>(data))
  },

  /**
   * The page a location belongs to, with that location in `location`: how a
   * Map pin, a travel place or a map search hit opens a Business page.
   */
  async getPageByLocation(locationId: string): Promise<PageDetails> {
    const data = await request<unknown>({ url: `${PAGES}/by-location/${locationId}` })
    return normalizePage(camelize<PageDetails>(data))
  },

  // ---- Business page locations (page admins write, everyone reads) ----

  async getPageLocations(pageId: string): Promise<{ data: PageLocation[]; total: number }> {
    const data = await request<unknown>({ url: `${PAGES}/${pageId}/locations` })
    const response = camelize<{ data?: PageLocation[] | null; total?: number | null }>(data)
    const locations = (response.data ?? []).map(normalizeLocation)
    return { data: locations, total: response.total ?? locations.length }
  },

  /**
   * Adds a location (Business pages only; errors: page_not_business,
   * page_location_limit_reached, page_location_type_required,
   * page_location_pin_required). Pictures travel afterwards through
   * pageLocationMedia upload sessions with the location as entity.
   */
  async createPageLocation(pageId: string, input: SavePageLocationInput): Promise<PageLocation> {
    const data = await request<unknown>({
      method: 'POST',
      url: `${PAGES}/${pageId}/locations`,
      data: locationBody(input),
    })
    return normalizeLocation(camelize<PageLocation>(data))
  },

  async updatePageLocation(
    pageId: string,
    locationId: string,
    input: SavePageLocationInput,
  ): Promise<PageLocation> {
    const data = await request<unknown>({
      method: 'PUT',
      url: `${PAGES}/${pageId}/locations/${locationId}`,
      data: locationBody(input),
    })
    return normalizeLocation(camelize<PageLocation>(data))
  },

  /** Drops a location and, server-side, its pictures. */
  async deletePageLocation(pageId: string, locationId: string): Promise<void> {
    await request({ method: 'DELETE', url: `${PAGES}/${pageId}/locations/${locationId}` })
  },

  /**
   * Ticks or unticks "I've been here" on a location (a hidden one fails with
   * poi_not_found). Ticking also adds the place's country to the travel map.
   */
  async setLocationVisited(locationId: string, visited: boolean): Promise<boolean> {
    const data = await request<{ visited?: boolean }>({
      method: 'POST',
      url: `${PAGES}/by-location/${locationId}/visited`,
      data: { visited },
    })
    return data?.visited ?? visited
  },

  /**
   * The caller's stars on a location (a like IS the rating), with an
   * optional comment (up to 500 characters); the server may refuse a change
   * while the rating is locked (poi_rating_locked).
   */
  async rateLocation(
    locationId: string,
    stars: number | null,
    comment?: string | null,
  ): Promise<{
    myStars: number | null
    myRatingLockedUntil: string | null
    myComment: string | null
  }> {
    const trimmed = comment?.trim() ?? ''
    const data = await request<Record<string, unknown>>(
      stars == null
        ? { method: 'DELETE', url: `${PAGES}/by-location/${locationId}/rating` }
        : {
            method: 'POST',
            url: `${PAGES}/by-location/${locationId}/rating`,
            data: trimmed ? { stars, comment: trimmed } : { stars },
          },
    )
    const myStars = data?.my_stars ?? data?.myStars
    const lockedUntil = data?.my_rating_locked_until ?? data?.myRatingLockedUntil
    const myComment = data?.my_comment ?? data?.myComment
    return {
      myStars: typeof myStars === 'number' ? myStars : null,
      myRatingLockedUntil: typeof lockedUntil === 'string' ? lockedUntil : null,
      myComment: typeof myComment === 'string' && myComment ? myComment : null,
    }
  },

  /** A location's ratings with their comments, newest first (404 when the location is hidden). */
  async getLocationRatings(
    locationId: string,
    skip = 0,
    take = 20,
  ): Promise<{ data: LocationRating[]; total: number }> {
    const data = await request<unknown>({
      url: `${PAGES}/by-location/${locationId}/ratings`,
      params: { skip, take },
    })
    const response = camelize<{ data?: LocationRating[] | null; total?: number | null }>(data)
    return { data: response.data ?? [], total: response.total ?? 0 }
  },

  async getPagePosts(
    pageId: string,
    skip = 0,
    take = 21,
  ): Promise<{ data: ProfilePostSummary[]; total: number }> {
    const data = await request<unknown>({
      url: `${PAGES}/${pageId}/posts`,
      params: { skip, take },
    })
    return camelize<{ data: ProfilePostSummary[]; total: number }>(data)
  },

  /**
   * The page's posts as full feed cards, newest first: the same payload as
   * the feed and the single-post endpoint, decoded the same way.
   */
  async getPageFeed(
    pageId: string,
    skip = 0,
    take = 10,
  ): Promise<{ data: FeedPost[]; total: number }> {
    const data = await request<unknown>({
      url: `${PAGES}/${pageId}/feed`,
      params: { skip, take },
    })
    return camelize<{ data: FeedPost[]; total: number }>(data)
  },

  async togglePageFollow(pageId: string): Promise<boolean> {
    const data = await request<Record<string, unknown>>({
      method: 'POST',
      url: `${PAGES}/${pageId}/follow`,
    })
    return Boolean(data?.is_following ?? data?.isFollowing)
  },

  /** Explicit unfollow (never a toggle): the "Pages you follow" list. */
  async unfollowPage(pageId: string): Promise<void> {
    await request({ method: 'DELETE', url: `${PAGES}/${pageId}/follow` })
  },

  /** Pages the caller follows, newest follow first. */
  async getFollowedPages(
    skip = 0,
    take = 30,
  ): Promise<{ data: ProfilePageSummary[]; total: number }> {
    const data = await request<unknown>({
      url: `${PAGES}/my-followed`,
      params: { skip, take },
    })
    return camelize<{ data: ProfilePageSummary[]; total: number }>(data)
  },

  async createPage(input: {
    name: string
    username: string
    bio?: string
    category?: string
    website?: string
    /** Alpha-2 country; omitted = the server defaults to the owner's country. */
    countryCode?: string
    /** Region audience (one CountryRegions flag) instead of a country. */
    region?: number | null
    /** PageKind: 0 topic (the default), 20 business, 30 business chain. */
    kind?: number
    /** Business kinds: the kind of place (required there). */
    locationType?: number | null
    /** The library track looping under the page (every kind). */
    soundId?: string | null
    /** Search-only words, up to 1024 characters. */
    keywords?: string
  }): Promise<string> {
    const data = await request<Record<string, unknown>>({
      method: 'POST',
      url: `${PAGES}/create`,
      data: {
        name: input.name,
        username: input.username,
        kind: input.kind ?? PAGE_KIND.topic,
        ...(input.bio ? { bio: input.bio } : {}),
        ...(input.category ? { category: input.category } : {}),
        ...(input.website ? { website: input.website } : {}),
        // Country XOR region: the region wins server-side when both travel.
        ...(input.region != null
          ? { region: input.region }
          : input.countryCode
            ? { country_code: input.countryCode }
            : {}),
        ...(input.locationType != null ? { location_type: input.locationType } : {}),
        ...(input.soundId ? { sound_id: input.soundId } : {}),
        ...(input.keywords ? { keywords: input.keywords } : {}),
      },
    })
    const pageId = data?.page_id ?? data?.pageId
    if (typeof pageId !== 'string') throw new Error('Missing pageId in create-page response')
    return pageId
  },

  /**
   * Edits an existing page - the @username is immutable. Any page admin. A
   * new audience only applies to posts published from now on.
   */
  async updatePage(
    pageId: string,
    input: {
      name: string
      bio?: string
      category?: string
      website?: string
      countryCode?: string
      /** Region audience (one CountryRegions flag) instead of a country. */
      region?: number | null
      /** Business kinds: a new kind of place, copied onto every location (null = unchanged). */
      locationType?: number | null
      /** A new library track (omitted/null = unchanged). */
      soundId?: string | null
      /** Drops the track (wins over soundId). */
      clearSound?: boolean
      /**
       * The existing covers to keep, in order (omitted = unchanged, [] =
       * none). New covers travel through pageCover upload sessions and are
       * appended server-side.
       */
      coverUrls?: string[]
      /** Search-only words: omitted = unchanged, "" = cleared. */
      keywords?: string
    },
  ): Promise<void> {
    await request({
      method: 'PUT',
      url: `${PAGES}/${pageId}`,
      data: {
        name: input.name,
        ...(input.bio !== undefined ? { bio: input.bio } : {}),
        ...(input.category !== undefined ? { category: input.category } : {}),
        ...(input.website !== undefined ? { website: input.website } : {}),
        // Country XOR region: the region wins server-side when both travel.
        ...(input.region != null
          ? { region: input.region }
          : input.countryCode !== undefined
            ? { country_code: input.countryCode }
            : {}),
        ...(input.locationType != null ? { location_type: input.locationType } : {}),
        ...(input.clearSound ? { clear_sound: true } : input.soundId ? { sound_id: input.soundId } : {}),
        ...(input.coverUrls !== undefined ? { cover_urls: input.coverUrls } : {}),
        ...(input.keywords !== undefined ? { keywords: input.keywords } : {}),
      },
    })
  },

  /** Deletes a page and, server-side, every post on it (the owner only). */
  async deletePage(pageId: string): Promise<void> {
    await request({ method: 'DELETE', url: `${PAGES}/${pageId}` })
  },

  // ---- Page admins (owner-managed) ----

  async getPageAdmins(pageId: string): Promise<PageAdmin[]> {
    const data = await request<unknown>({ url: `${PAGES}/${pageId}/admins` })
    return camelize<PageAdmin[]>(data)
  },

  /** Owner only. Editors (role 20) may publish on the page; nothing else. */
  async addPageAdmin(
    pageId: string,
    userId: string,
    role: number = PAGE_ROLE.editor,
  ): Promise<void> {
    await request({
      method: 'POST',
      url: `${PAGES}/${pageId}/admins`,
      data: { user_id: userId, role },
    })
  },

  /** Owner only. */
  async removePageAdmin(pageId: string, userId: string): Promise<void> {
    await request({ method: 'DELETE', url: `${PAGES}/${pageId}/admins/${userId}` })
  },

  /** Availability of a page handle (Check button in the create form). */
  async checkPageUsername(username: string): Promise<boolean> {
    const data = await request<Record<string, unknown>>({
      url: `${PAGES}/username-check`,
      params: { username },
    })
    return Boolean(data?.available)
  },

  // ---- Ranking / analytics ----

  async getTopRankings(country?: string | null): Promise<RankingEntry[]> {
    const data = await request<unknown>({
      url: '/api/eleva/wallet/leaderboard',
      params: { limit: 50, ...(country ? { country } : {}) },
    })
    return camelize<RankingEntry[]>(data)
  },

  async getCreatorAnalytics(userId: string, period: AnalyticsPeriod): Promise<AnalyticsData> {
    const data = await request<unknown>({
      url: '/api/eleva/analytics/creator',
      params: { user_id: userId, period },
    })
    return camelize<AnalyticsData>(data)
  },
}
